import type {
  LayoutRecognizedText,
  LayoutReference,
  LayoutReferenceAnalysis,
  LayoutReferenceCorrections,
  LayoutReferenceRect,
  LayoutReferenceTextAction,
} from './types'

export const FULL_REFERENCE_RECT: LayoutReferenceRect = { x: 0, y: 0, width: 1, height: 1 }

const record = (value: unknown): Record<string, any> =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, any>) : {}

const strings = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === 'string') : []

function stableTextId(value: string, index: number): string {
  let hash = 0x811c9dc5
  for (let cursor = 0; cursor < value.length; cursor += 1) {
    hash ^= value.charCodeAt(cursor)
    hash = Math.imul(hash, 0x01000193)
  }
  return `visible-text-${index}-${(hash >>> 0).toString(36)}`
}

export function isNormalizedReferenceRect(value: unknown): value is LayoutReferenceRect {
  const rect = record(value)
  const x = Number(rect.x)
  const y = Number(rect.y)
  const width = Number(rect.width)
  const height = Number(rect.height)
  return (
    [x, y, width, height].every(Number.isFinite) &&
    x >= 0 &&
    y >= 0 &&
    width > 0 &&
    height > 0 &&
    x + width <= 1 &&
    y + height <= 1
  )
}

export function normalizeReferenceRect(
  value: unknown,
  fallback: LayoutReferenceRect | null = null,
): LayoutReferenceRect | null {
  if (!isNormalizedReferenceRect(value)) return fallback ? { ...fallback } : null
  const rect = record(value)
  return {
    x: Number(rect.x),
    y: Number(rect.y),
    width: Number(rect.width),
    height: Number(rect.height),
  }
}

function normalizeRecognizedTexts(value: Record<string, any>): LayoutRecognizedText[] {
  if (Array.isArray(value.visibleText)) {
    return value.visibleText.flatMap((entry: unknown, index: number) => {
      if (typeof entry !== 'string') return []
      return [{ id: stableTextId(entry, index), value: entry }]
    })
  }
  if (!Array.isArray(value.recognizedTexts)) return []
  return value.recognizedTexts.flatMap((entry: unknown, index: number) => {
    const row = record(entry)
    const text = String(row.value ?? row.text ?? '')
    if (!text) return []
    return [{ id: String(row.id || stableTextId(text, index)), value: text }]
  })
}

/** Removes provider metadata and normalizes both the persisted envelope and legacy flat shape. */
export function normalizeReferenceAnalysis(raw: unknown): LayoutReferenceAnalysis | null {
  const envelope = record(raw)
  const value = Object.keys(record(envelope.analysis)).length ? record(envelope.analysis) : envelope
  if (!Object.keys(value).length) return null
  const confidence = Number(value.confidence)
  const designRegion = normalizeReferenceRect(value.designRegion)
  return {
    theme: value.theme == null ? undefined : String(value.theme),
    characters: strings(value.characters),
    palette: strings(value.palette),
    style: value.style == null ? undefined : String(value.style),
    visibleText: normalizeRecognizedTexts(value),
    advertisingElements: strings(value.advertisingElements ?? value.advertising),
    composition: value.composition == null ? undefined : String(value.composition),
    summary: value.summary == null ? undefined : String(value.summary),
    confidence:
      Number.isFinite(confidence) && confidence >= 0 && confidence <= 1 ? confidence : undefined,
    designRegion,
    requiresRegionConfirmation:
      value.requiresRegionConfirmation === true ||
      (Number.isFinite(confidence) && confidence >= 0 && confidence < 0.7),
  }
}

export function normalizeReferenceCorrections(raw: unknown): LayoutReferenceCorrections | null {
  const value = record(raw)
  if (!Object.keys(value).length) return null
  const rows = Array.isArray(value.textActions)
    ? value.textActions
    : Array.isArray(value.recognizedTexts)
      ? value.recognizedTexts
      : []
  const textActions = rows.flatMap((entry: unknown) => {
    const row = record(entry)
    const id = String(row.id || '').trim()
    const action = String(row.action)
    if (!id || !['keep', 'replace', 'remove'].includes(action)) return []
    const replacement = row.replacement ?? (action === 'replace' ? row.value : undefined)
    return [
      {
        id,
        action: action as LayoutReferenceTextAction['action'],
        ...(replacement == null ? {} : { replacement: String(replacement) }),
      },
    ]
  })
  return {
    designRegion: normalizeReferenceRect(value.designRegion),
    textActions,
  }
}

export function referenceTextActions(reference: LayoutReference): LayoutReferenceTextAction[] {
  const saved = new Map((reference.corrections?.textActions || []).map((row) => [row.id, row]))
  return (reference.analysis?.visibleText || []).map((text) => ({
    id: text.id,
    action: saved.get(text.id)?.action || 'keep',
    ...(saved.get(text.id)?.replacement == null
      ? {}
      : { replacement: saved.get(text.id)?.replacement }),
  }))
}

export function referenceAdjustmentIssue(
  crop: LayoutReferenceRect,
  designRegion: LayoutReferenceRect,
  textActions: LayoutReferenceTextAction[],
): string | null {
  if (!isNormalizedReferenceRect(crop)) return 'Crop должен находиться внутри изображения'
  if (!isNormalizedReferenceRect(designRegion))
    return 'Область дизайна должна находиться внутри изображения'
  if (textActions.some((row) => row.action === 'replace' && !row.replacement?.trim()))
    return 'Для замены распознанного текста укажите новое значение'
  return null
}

export function sameReferenceRect(
  left: LayoutReferenceRect | null,
  right: LayoutReferenceRect,
): boolean {
  return Boolean(
    left &&
    left.x === right.x &&
    left.y === right.y &&
    left.width === right.width &&
    left.height === right.height,
  )
}
